import Map, { Marker } from 'react-map-gl';
import { useCallback, useEffect, useState } from 'react';
import { Avatar, Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { userService, pinsService, geocodingService, apiService } from '../../services';
import { PinnerProData } from '../../interfaces/Pinners';
import { PinIcon, PinPoco, PinAbb, PinPro, PinnerProIcon } from '../../assets/icons/Index';
import InfoMarkerModal from '../../components/specific/modal/InfoMarkerModal';
import { MarkerData } from '../../types/markerData';
import { useTranslation } from 'react-i18next';
import DefaultMarkerModal from '../../components/specific/modal/DefaultMarkerModal';
import InstagramIcon from '@mui/icons-material/Instagram';
import GppGoodIcon from '@mui/icons-material/GppGood';
import GppBadIcon from '@mui/icons-material/GppBad';
import LanguageIcon from '@mui/icons-material/Language';
import { useNavigate, useParams } from 'react-router-dom';
import { ConsultancyDataProps } from '../../interfaces/Billing';
import { CreditCardsPlan } from '../../enums/CreditsCard';
import { Plan } from '../../interfaces/Consultancy';
import CreditCards from '../../components/specific/cards/CreditCards';
import LoaderGif from '../../assets/gifs/loader.gif'
import { PIN_TYPES_OPTIONS } from '../../enums/MarkerOptions';
import ErrorIcon from '@mui/icons-material/Error';
import { AppRoutes } from '../../enums/Approutes';
import { getConfig } from '../../config/env';

const mapPlanTypeToPlanName = (type: number): CreditCardsPlan => {
    switch (type) {
        case Plan.FREE:
            return CreditCardsPlan.FREE;
        case Plan.LIGHT:
            return CreditCardsPlan.LIGHT;
        case Plan.PLUS:
            return CreditCardsPlan.PLUS;
        default:
            return CreditCardsPlan.FREE;
    }
};

const PinnerProProfilePage = () => {
    const mapboxToken = getConfig().MAPBOX_TOKEN;
    const { t } = useTranslation();
    const { userId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState<PinnerProData | null>(null);
    const [mapStyle] = useState<string>("mapbox://styles/mapbox/light-v9");
    const [consultancyPlans, setConsultancyPlans] = useState<ConsultancyDataProps[]>([]);
    const [markers, setMarkers] = useState<MarkerData[]>([]);
    const [popupInfo, setPopupInfo] = useState<MarkerData | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isDefaultMarkerModalOpen, setIsDefaultMarkerModalOpen] = useState<boolean>(false);
    const [residencePlace, setResidencePlace] = useState<string>('');
    const [publicResidence, setPublicResidence] = useState<boolean>(false);
    const [residenceCoordinates, setResidenceCoordinates] = useState<{ longitude: number; latitude: number } | null>(null);
    const [initialViewState, setInitialViewState] = useState({
        longitude: 12.646361,
        latitude: 42.504154,
        zoom: 4,
    });
    const [loading, setLoading] = useState(true);

    const getPinComponent = (option: number) => {
        switch (option) {
            case 0:
                return PinPoco;
            case 1:
                return PinAbb;
            case 2:
                return PinIcon;
            case 3:
                return PinPro;
            default:
                return PinPoco;
        }
    };

    const fetchMarkers = useCallback(async () => {
        if (!userId) return;
        try {
            const markersData = await pinsService.getUserMarkers(userId);
            setMarkers(markersData);
        } catch (error) {
            console.error('Error fetching markers:', error);
        }
    }, [userId]);

    useEffect(() => {
        const fetchUser = async () => {
            if (!userId) return;
            try {
                const userData = await userService.getUserProfile(userId);
                setUser(userData);
                const residence = userData.residence;
                const isPublic = (userData as any).public_residence ?? false;
                setPublicResidence(isPublic);
                if (residence && isPublic) {
                    setResidencePlace(residence);
                    try {
                        const coords = await geocodingService.getEnglishCoordinates(residence);
                        if (coords && coords.coordinates) {
                            const [longitude, latitude] = coords.coordinates;
                            setResidenceCoordinates({ longitude, latitude });
                            setInitialViewState({
                                longitude,
                                latitude,
                                zoom: 4,
                            });
                        }
                    } catch (error) {
                        console.error('Errore nella geocodifica della residenza:', error);
                    }
                }
            } catch (error) {
                console.error('Error fetching user:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchMarkers();
        fetchUser();
    }, [userId]);

    useEffect(() => {
        const fetchConsultancies = async () => {
            if (!userId) return;
            try {
                const response = await apiService.get(`/users/pinnerpro/${userId}/consultancy/`);
                const plans = response.data;
                const namedPlans = plans.map((plan: ConsultancyDataProps) => ({
                    ...plan,
                    planName: plan.type !== undefined ? mapPlanTypeToPlanName(plan.type) : CreditCardsPlan.FREE
                }));
                setConsultancyPlans(namedPlans);
            } catch (e) {
                setConsultancyPlans([]);
            }
        };
        fetchConsultancies();
    }, [userId]);


    const handleMarkerClick = useCallback((marker: MarkerData) => {
        setPopupInfo(marker);
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setPopupInfo(null);
    };

    const handleResidenceMarkerClick = () => {
        setIsDefaultMarkerModalOpen(true);
    };

    const formatResidence = (residence: string) => {
        if (!residence) return '';
        const parts = residence.split(',').map(p => p.trim());
        if (parts.length < 2) return residence;
        const city = parts[parts.length - 2];
        const country = parts[parts.length - 1];
        return city === country ? city : `${city}, ${country}`;
    };

    const getDecadeFromBirthDate = (birthDate: string | undefined) => {
        if (!birthDate) return '';

        try {
            const year = new Date(birthDate).getFullYear();
            const decade = Math.floor(year / 10) * 10;

            if (decade >= 2000) {
                return `${t("pinner_pro:years")} '${(decade % 100).toString().padStart(2, '0')}`;
            } else {
                return `${t("pinner_pro:years")} '${decade % 100}`;
            }
        } catch (error) {
            console.error('Errore nel parsing della data di nascita:', error);
            return '';
        }
    };

    // Handle Pinner Pro Heading message
    const handlePinnerProHeading = (user: PinnerProData) => {
        const noProject = user.project_name === "";

        if (noProject) {
            return {
                leftKey: 'pinner_pro:pinner_hello_left',
                name: user.first_name,
                rightKey: 'pinner_pro:pinner_hello_right',
                key1: 'pinner_pro:info_pro_1',
                key2: 'pinner_pro:info_pro_2',
                key3: 'pinner_pro:info_pro_3',
                key4: 'pinner_pro:info_pro_4'
            };
        }

        if (user.is_solo_project === true) {
            return {
                leftKey: 'pinner_pro:pinner_hello_solo_left',
                name: user.project_name!,
                rightKey: 'pinner_pro:pinner_hello_solo_right',
                key1: 'pinner_pro:info_solo_1',
                key2: 'pinner_pro:info_solo_2',
                key3: 'pinner_pro:info_solo_3',
                key4: null
            };
        }

        if (user.is_solo_project === false) {
            return {
                leftKey: 'pinner_pro:project_hello_left',
                name: user.project_name!,
                rightKey: 'pinner_pro:project_hello_right',
                key1: 'pinner_pro:info_project_1',
                key2: 'pinner_pro:info_project_2',
                key3: 'pinner_pro:info_project_3',
                key4: null
            };
        };
    };


    if (loading) {
        return <div className='w-screen h-screen flex justify-center items-center'>
            <img src={LoaderGif} alt='' className='w-16 h-auto object-cover' />
        </div>;
    }
    if (!user) return <div className='min-h-screen w-full flex flex-col items-center justify-center'>
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
                textAlign: 'center'
            }}
        >
            <ErrorIcon
                sx={{
                    fontSize: 80,
                    color: '#f44336',
                    mb: 3
                }}
            />
            <Typography variant="body1" sx={{ mb: 4, color: '#666' }}> {t('pinners:noResults')}</Typography>
            <Button
                variant="contained"
                onClick={() => navigate(AppRoutes.SETTINGS)}
                sx={{
                    backgroundColor: '#E9495A',
                    '&:hover': { backgroundColor: '#E6263B' },
                    px: 4,
                    py: 1.5
                }}
            >
                {t('pinner_pro:back_to_settings')}
            </Button>
        </Box>
    </div>;

    return (
        <div className='w-full min-h-screen p-6 md:py-10 md:px-24 flex flex-col items-center gap-8'>
            {/* Header */}
            <div className='w-full flex items-center gap-6'>
                <div className='relative'>
                    <Avatar
                        alt={user.first_name + ' ' + user.last_name}
                        src={user.profile_picture}
                        sx={{ width: 70, height: 70 }}
                    />
                    <div style={{ position: 'absolute', bottom: -2, right: -5, zIndex: 10 }}>
                        <PinnerProIcon className='' width={25} height={25} />
                    </div>
                </div>
                {(() => {
                    const headingMessage = handlePinnerProHeading(user);
                    if (!headingMessage) return null;

                    return (
                        <Typography variant='h2' className='text-lg font-bold'>
                            {t(headingMessage.leftKey)} <span className='text-primary'>{headingMessage.name}</span> {t(headingMessage.rightKey)}
                        </Typography>
                    );
                })()}
            </div>

            {/* Bio */}
            <div className='w-full flex flex-col items-start gap-2'>
                <div>
                    <Typography variant='h6' className='font-bold text-md'>{t('pinner_pro:bio_title')}</Typography>
                </div>
                <div>
                    {user.bio ? (
                        <Typography variant='body1'>{user.bio}</Typography>
                    ) : (
                        <Typography variant='body1'>{t('pinner_pro:no_bio')}</Typography>
                    )}
                </div>

                {/* InfoBox */}
                <div className='w-full h-auto p-6 bg-[#f9fafb] rounded-md border border-[#e4e7ec] mt-4'>
                    <div className='w-full h-auto flex items-center gap-2 mb-5 md:mb-0'>
                        {user.is_pro_verified ? <GppGoodIcon sx={{ color: '#34A853' }} /> : <GppBadIcon sx={{ color: '#E6263B' }} />}
                        <Typography variant='body1'>{user.is_pro_verified ? t('pinner_pro:pro_verified') : t('pinner_pro:pro_not_verified')}</Typography>
                    </div>
                    <div className='w-full h-auto flex flex-col md:flex-row items-start gap-6 md:items-center md:justify-between md:gap-0'>
                        <div className='w-full md:w-2/3 h-auto'>
                            {(() => {
                                const infoKeys = handlePinnerProHeading(user);
                                const hasProjectData = infoKeys?.key4 === null;
                                if (!infoKeys) return null;

                                return (
                                    <Typography variant='body1'>
                                        {!hasProjectData ? (
                                            <>
                                                {t(infoKeys.key1)}
                                                <span className='font-bold'>{formatResidence(user.residence)}</span>
                                                {t(infoKeys.key2)}
                                                <span className='font-bold'>{getDecadeFromBirthDate(typeof user.birth_date === 'string' ? user.birth_date : user.birth_date?.toISOString?.() ?? undefined)}</span>
                                                {t(infoKeys.key3)}
                                                <span className='font-bold'>{user.spoken_languages?.join(', ')}</span>
                                                {infoKeys.key4 && t(infoKeys.key4)}
                                                <span className='font-bold'>{Array.isArray(user.interested_to) && user.interested_to.length > 0
                                                    ? user.interested_to.slice(0, 3).map(i => {
                                                        const found = PIN_TYPES_OPTIONS.find(opt => opt.value === i);
                                                        return found ? t(found.labelKey) : i;
                                                    }).join(', ')
                                                    : t('pinner_pro:no_interested_to')}</span>
                                            </>
                                        ) : (
                                            <>
                                                <span className='font-bold'>{user.project_name}</span> {t(infoKeys.key1)} <span className='font-bold'>{user.project_start_year}</span>
                                                {', '}
                                                {t(infoKeys.key2)}
                                                <span className='font-bold'>{user.spoken_languages?.join(', ')}</span>
                                                {t(infoKeys.key3)}
                                                <span className='font-bold'>{Array.isArray(user.interested_to) && user.interested_to.length > 0
                                                    ? user.interested_to.slice(0, 3).map(i => {
                                                        const found = PIN_TYPES_OPTIONS.find(opt => opt.value === i);
                                                        return found ? t(found.labelKey) : i;
                                                    }).join(', ')
                                                    : t('pinner_pro:no_interested_to')}</span>
                                            </>
                                        )}
                                    </Typography>
                                );
                            })()}
                        </div>

                        <div className='w-full md:w-1/3 h-auto flex flex-col items-start md:items-center gap-2'>
                            <div className="w-full h-auto flex items-center justify-start md:justify-center">
                                {user.is_solo_project || user.project_name === "" ? (
                                    <Typography variant='body1' className='font-medium'>{t('pinner_pro:social_title')}</Typography>
                                ) : (
                                    <Typography variant='body1' className='font-medium'>{t('pinner_pro:social_project_title')}</Typography>
                                )}
                            </div>
                            <div className='w-full h-auto flex items-center justify-start md:justify-center gap-3'>
                                <IconButton onClick={() => window.open(user.instagram, '_blank')}>
                                    <InstagramIcon sx={{ width: 30, height: 30, color: '#999A9B', '&:hover': { color: '#E9495A' } }} />
                                </IconButton>
                                {user.website && (
                                    <IconButton onClick={() => window.open(user.website, '_blank')}>
                                        <LanguageIcon sx={{ width: 30, height: 30, color: '#999A9B', '&:hover': { color: '#E9495A' } }} />
                                    </IconButton>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Divider className="w-full" />

            {/* Map */}
            <div className='w-full flex flex-col items-start gap-2'>
                <div>
                    <Typography variant='h6' className='font-bold text-md'>{t('pinner_pro:map_title')}</Typography>
                </div>
                <div style={{ width: '100%', minHeight: 400 }}>
                    <Map
                        initialViewState={initialViewState}
                        style={{ flex: 1, borderRadius: '1rem', height: '700px' }}
                        mapboxAccessToken={mapboxToken}
                        mapStyle={mapStyle}
                    >
                        {markers.map((marker, index) => {
                            const Icon = getPinComponent(marker.knowledge_level);
                            return (
                                <Marker
                                    key={index}
                                    longitude={marker.longitude}
                                    latitude={marker.latitude}
                                    onClick={() => handleMarkerClick(marker)}
                                >
                                    <Icon className='' />
                                </Marker>
                            )
                        })}

                        {residenceCoordinates && publicResidence && (
                            <Marker
                                longitude={residenceCoordinates.longitude}
                                latitude={residenceCoordinates.latitude}
                                onClick={(e) => {
                                    e.originalEvent.stopPropagation();
                                    handleResidenceMarkerClick();
                                }}
                            >
                                <PinPro className="" />
                            </Marker>
                        )}
                    </Map>
                </div>
            </div>

            <Divider className="w-full" />

            {/* Consulenze */}
            <div className='w-full flex flex-col items-start gap-2'>
                <div>
                    <Typography variant='h6' className='font-bold text-md'>{t('pinner_pro:consultancy_title')}</Typography>
                    <Typography variant='body1'>{t('pinner_pro:consultancy_description')}</Typography>
                </div>
                <div className={`w-full flex flex-col md:flex-row gap-6 md:gap-20 ${consultancyPlans.length === 0 ? 'justify-start' : 'justify-center'} items-center mt-6`}>
                    {consultancyPlans.map((plan) => (
                        <div key={plan.id} className="flex-shrink-0">
                            <CreditCards
                                id={plan.id}
                                price={plan.price}
                                extras={plan.extras}
                                planName={plan.planName}
                                pinnerProId={userId}
                                credits={plan.price.toString()}
                                pinner_pro={user?.first_name + ' ' + user?.last_name}
                                project_name={user?.project_name}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Modali */}
            {isDefaultMarkerModalOpen && (
                <DefaultMarkerModal
                    open={isDefaultMarkerModalOpen}
                    onClose={() => setIsDefaultMarkerModalOpen(false)}
                    place={residencePlace}
                    text={t('pinMap:profile_marker_message')}
                />
            )}

            {popupInfo && (
                <InfoMarkerModal
                    marker={popupInfo}
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    hidePrivateContent={true}
                />
            )}
        </div>
    );
};

export default PinnerProProfilePage;
